import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { AbilityDTO, IAttributes, IItemInstanceDTO } from '@hexcrawl/shared-utils';
import { ItemIcon } from '../inventory/ItemIcon';
import { StackBadge } from '../inventory/StackBadge';
import { abilityService } from '../../services/abilityService';
import { useGameStore } from '../../stores/gameStore';
import { summarizeAbilityEffect } from '../../utils/abilitySummary';
import { consumableContextLabel, getCastAbilityId, isUsableInCombat, summarizeConsumable } from '../../utils/consumableSummary';

const POPOVER_ITEM_SIZE_PX = 40;
const POPOVER_MAX_COLUMNS = 3;
const POPOVER_COLUMN_WIDTH_PX = 200;
const POPOVER_MAX_HEIGHT_PX = 260;

interface UseItemPopoverProps {
  /** Every unequipped item in the bag; the popover picks the consumables out of it. */
  inventory: IItemInstanceDTO[];
  /** True while a request is in flight or the playback queue is running. */
  disabled: boolean;
  /** True once this turn's ability action is spent, which locks out `usesTurn` items. */
  abilityActionSpent: boolean;
  /**
   * Element enclosing both the popover and the button that opened it. A click inside it
   * is not "outside" — otherwise the toggle button would close and reopen in one click.
   */
  boundaryRef: React.RefObject<HTMLElement | null>;
  onSelect: (item: IItemInstanceDTO) => void;
  onClose: () => void;
}

export const UseItemPopover: React.FC<UseItemPopoverProps> = ({ inventory, disabled, abilityActionSpent, boundaryRef, onSelect, onClose }) => {
  const rootRef = useRef<HTMLDivElement>(null);
  // One row per instance: identical consumables already share a single stacked row.
  const entries = inventory.filter(item => item.def.consumable);

  const character = useGameStore((s) => s.gameState?.character);

  // Items that cast a spell describe it with the spell's own numbers, so the defs have
  // to be on hand. Fetching here also warms the synchronous cache the scene's targeting
  // and highlighting read from once one of these is picked.
  // `entries` is a fresh array every render, so the ids are collapsed to a string first
  // — that is what stays stable across renders and keeps the fetch from re-firing.
  const castAbilityKey = entries.map(item => getCastAbilityId(item.def) ?? '').join(',');
  const castAbilityIds = useMemo(
    () => Array.from(new Set(castAbilityKey.split(',').filter(Boolean))),
    [castAbilityKey],
  );
  const [castAbilities, setCastAbilities] = useState<Record<string, AbilityDTO>>({});
  useEffect(() => {
    if (castAbilityIds.length === 0) return;
    let cancelled = false;
    Promise.all(castAbilityIds.map(id => abilityService.getAbility(id)))
      .then((fetched) => {
        if (cancelled) return;
        setCastAbilities(Object.fromEntries(fetched.map(a => [a.id, a])));
      })
      .catch(err => console.error('Failed to fetch consumable abilities:', err));
    return () => { cancelled = true; };
  }, [castAbilityIds]);

  // Click-outside to dismiss. Mousedown rather than click so the popover closes before
  // an underlying tile click is processed by the scene.
  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (boundaryRef.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [onClose, boundaryRef]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div
      ref={rootRef}
      style={{
        position: 'absolute',
        bottom: 'calc(100% + 8px)',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 10,
        display: 'grid',
        gridTemplateColumns: `repeat(${Math.min(POPOVER_MAX_COLUMNS, Math.max(1, entries.length))}, ${POPOVER_COLUMN_WIDTH_PX}px)`,
        gap: 6,
        maxHeight: POPOVER_MAX_HEIGHT_PX,
        overflowY: 'auto',
        padding: 8,
        background: 'rgba(12, 20, 32, 0.97)',
        border: '2px solid #5aa0ff',
        borderRadius: 8,
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.6)',
      }}
    >
      {entries.length === 0 && (
        <div style={{ color: '#7799bb', fontSize: 13, padding: '6px 8px', whiteSpace: 'nowrap' }}>
          Nothing in your bag can be used here.
        </div>
      )}

      {entries.map((item) => {
        const consumable = item.def.consumable!;
        const contextLabel = consumableContextLabel(consumable);
        // A `usesTurn` item competes for the same action a spell does, so once that is
        // spent the item is locked out for the rest of the turn just as the bar is.
        const actionBlocked = consumable.usesTurn && abilityActionSpent;
        const usable = isUsableInCombat(item.def) && !actionBlocked;
        const clickable = usable && !disabled;

        const castAbility = castAbilities[getCastAbilityId(item.def) ?? ''];
        const castSummary = castAbility
          ? summarizeAbilityEffect(
              castAbility,
              character?.skills as Record<string, number | undefined> | undefined,
              character?.attributes as IAttributes | undefined,
            )
          : null;

        const disabledReason = actionBlocked
          ? 'You have already used an ability this turn'
          : isUsableInCombat(item.def) ? undefined : 'Cannot use in combat';

        return (
          <button
            key={item.id}
            onClick={() => clickable && onSelect(item)}
            disabled={!clickable}
            title={disabledReason}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: 6,
              textAlign: 'left',
              background: 'rgba(20, 40, 60, 0.8)',
              border: '1px solid #2a4a6a',
              borderRadius: 6,
              color: '#d9ecff',
              cursor: clickable ? 'pointer' : 'not-allowed',
              opacity: usable ? (disabled ? 0.6 : 1) : 0.45,
            }}
          >
            {/* The icon carries the ×N badge in the same corner the bag grid uses. */}
            <span style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
              <ItemIcon atlas={item.def.icon.atlas} frame={item.def.icon.frame} size={POPOVER_ITEM_SIZE_PX} />
              <StackBadge quantity={item.quantity} />
            </span>
            <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
              <span style={{ fontSize: 12, fontWeight: 'bold' }}>{item.def.name}</span>
              <span style={{ fontSize: 11, color: '#aaddaa' }}>{summarizeConsumable(consumable)}</span>
              {castSummary && castAbility && (
                <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap', fontSize: 10 }}>
                  {castSummary.minDamage != null && castSummary.maxDamage != null && (
                    <span style={{ color: '#ff9966' }}>{castSummary.minDamage}–{castSummary.maxDamage} dmg</span>
                  )}
                  <span style={{ color: '#88c0ff' }}>range {castAbility.target.range}</span>
                  {castAbility.target.aoeRadius != null && (
                    <span style={{ color: '#88c0ff' }}>blast {castAbility.target.aoeRadius}</span>
                  )}
                </span>
              )}
              {consumable.usesTurn && (
                <span style={{ fontSize: 10, color: actionBlocked ? '#cc7777' : '#ffcc66' }}>
                  {actionBlocked ? 'Ability already used this turn' : 'Spends your action'}
                </span>
              )}
              {!isUsableInCombat(item.def) && contextLabel && (
                <span style={{ fontSize: 10, color: '#ddaa55' }}>{contextLabel}</span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
};
